import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const GoldParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);
      particles = Array.from({ length: 55 }, () => ({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        r: Math.random() * 1.8 + 0.4,
        vy: -(Math.random() * 0.25 + 0.06),
        vx: (Math.random() - 0.5) * 0.15,
        tw: Math.random() * Math.PI * 2,
      }));
    };

    const tick = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      for (const p of particles) {
        p.y += p.vy;
        p.x += p.vx;
        p.tw += 0.02;
        if (p.y < -5) { p.y = canvas.offsetHeight + 5; p.x = Math.random() * canvas.offsetWidth; }
        const alpha = 0.25 + Math.abs(Math.sin(p.tw)) * 0.55;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 175, 55, ${alpha})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };

    resize();
    tick();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const sigY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.10),transparent_55%)]" />
      <div className="halo-drift absolute -top-32 right-[8%] w-[480px] h-[480px] rounded-full bg-[#D4AF37]/10 blur-[130px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 pb-24 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center w-full">
        <motion.div style={{ y: textY, opacity: fade }}>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="font-mono-accent text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]"
            data-testid="hero-overtitle"
          >
            Dirigeants de PME · QVT · Accompagnement individuel
          </motion.p>

          <h1 className="font-serif-display mt-8 text-4xl sm:text-5xl lg:text-[4.4rem] font-medium leading-[1.04] tracking-tight text-white">
            <MaskedLine delay={0.5}>Le courage de diriger.</MaskedLine>
            <MaskedLine delay={0.65}>La force d'être</MaskedLine>
            <MaskedLine delay={0.8}>
              <span className="italic text-gold-gradient">accompagné.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            Psychologie du travail, neurosciences et vingt ans d'expérience en entreprise :
            Frédérique Vasseur accompagne celles et ceux qui portent une organisation —
            et celles et ceux qui traversent une vie — vers une performance durable et humaine.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-booking-button"
              onClick={() => scrollToId("#contact")}
              className="rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-semibold tracking-wide text-[#0A1120] transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-10px_rgba(212,175,55,0.5)]"
            >
              Réserver mon diagnostic offert
            </button>
            <button
              data-testid="hero-cta-approach-button"
              onClick={() => scrollToId("#approche")}
              className="rounded-full border border-slate-500/50 px-8 py-4 text-sm font-medium tracking-wide text-slate-200 transition-all duration-300 hover:border-[#D4AF37]/60 hover:text-[#F3E5AB]"
            >
              Découvrir l'approche
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: sigY }}
          className="relative hidden lg:flex justify-center"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.9, ease: EASE }}
        >
          <GoldParticles />
          <motion.img
            src="/assets/signature.png"
            alt="Signature de Frédérique Vasseur"
            data-testid="hero-signature-image"
            className="relative w-[380px] max-w-full object-contain drop-shadow-[0_0_60px_rgba(212,175,55,0.35)]"
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            animate={{ clipPath: "inset(0 0% 0 0)" }}
            transition={{ duration: 1.6, delay: 1.1, ease: EASE }}
          />
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#approche")}
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-400 hover:text-[#D4AF37] transition-colors"
        aria-label="Défiler vers le bas"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={22} />
        </motion.div>
      </motion.button>
    </section>
  );
}
